import type { ILoggerService } from './LoggerService';

export type HubListenerCallback = (...args: any[]) => void;

export interface IListenerService {
  on(eventName: string, callback: HubListenerCallback): string;
  off(listenerIds: string[]): void;
}

export abstract class AbstractListenerService implements IListenerService {
  constructor(protected logger: ILoggerService) {}

  protected listeners: {
    [eventName: string]: { [listenerId: string]: HubListenerCallback };
  } = {};

  /**
   * Generates a simple random identifier for a listener
   * @returns Random identifier string
   */
  private generateListenerId(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  /**
   * Registers a listener for a specific event
   * @param eventName Name of event
   * @param callback Callback function called when event occurs
   * @returns Listener identifier
   */
  public on(eventName: string, callback: HubListenerCallback): string {
    const listenerId = this.generateListenerId();
    // Create the event object if it doesn't exist
    if (!this.listeners[eventName]) {
      this.listeners[eventName] = {};
    }

    // Add the listener callback
    this.listeners[eventName][listenerId] = callback;

    // Allow the derived class to handle the new listener, if needed
    this.listenerAdded(eventName, this.listeners[eventName][listenerId]);

    this.logger.trace(
      `Registered listener '${listenerId}' for event '${eventName}'`,
    );

    return listenerId;
  }

  /**
   * Removes a listener for a specific event
   * @param listenerId Listener identifier
   */
  public off(listenerIds: string[]) {
    for (const listenerId of listenerIds) {
      const eventName = Object.keys(this.listeners).find(
        (event) => this.listeners[event][listenerId],
      );

      if (!eventName) {
        this.logger.warn(`Listener '${listenerId}' not found.`);
        return;
      }

      if (this.listeners[eventName] && this.listeners[eventName][listenerId]) {
        // Allow the derived class to remove the listener as well, if needed
        this.listenerRemoved(eventName, this.listeners[eventName][listenerId]);

        // Remove the listener callback
        delete this.listeners[eventName][listenerId];
        this.logger.trace(
          `Removed listener '${listenerId}' for event '${eventName}'`,
        );

        // Cleanup if there are no more listeners for the event
        if (Object.keys(this.listeners[eventName]).length === 0) {
          delete this.listeners[eventName];
        }
      }
    }
  }

  protected listenerAdded(
    eventName: string,
    callback: HubListenerCallback,
  ): void {
    // Can be overridden by derived classes to perform additional setup
  }

  protected listenerRemoved(
    eventName: string,
    callback: HubListenerCallback,
  ): void {
    // Can be overridden by derived classes to perform additional cleanup
  }

  /**
   * Broadcasts an event to all registered listeners
   * @param eventName Name of event
   * @param args Arguments passed to event listeners
   */
  protected broadcast(eventName: string, ...args: any[]) {
    if (this.listeners[eventName]) {
      Object.keys(this.listeners[eventName]).forEach((listenerId) => {
        const callback = this.listeners[eventName][listenerId];
        callback(...args);
      });
    }
  }
}
